import React, { useEffect, useState, useRef } from 'react';
import {
	TrashIcon,
	CheckCircleIcon,
	ClockIcon,
	ArchiveBoxIcon,
	GlobeAltIcon,
	LockClosedIcon,
	TagIcon,
	XMarkIcon,
	LinkIcon,
} from '@heroicons/react/24/outline';

import {
	createKbArticle,
	updateKbArticle,
	deleteKbArticle,
	publishKbArticle,
	archiveKbArticle,
	restoreKbArticle,
	getKbTemplates,
} from '../api/knowledgeBase';
import { StackableModal, FilterDropdown, Badge } from '../components/nextgen';
import { classNames } from '../components/helpers/functions';
import { prepareForEdit } from '../components/helpers/markdown';
import MarkdownEditor from '../components/MarkdownEditor';
import ManufacturerModelSelect from '../components/ManufacturerModelSelect';
import { uploadKbImage } from '../api/knowledgeBase';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'editKbArticle';

// KB Categories
const CATEGORIES = [
	{ value: 'troubleshooting', label: 'Troubleshooting' },
	{ value: 'maintenance', label: 'Maintenance' },
	{ value: 'howto', label: 'How-To' },
	{ value: 'faq', label: 'FAQ' },
	{ value: 'reference', label: 'Reference' },
	{ value: 'safety', label: 'Safety' },
];

// Scope Types
const SCOPE_TYPES = [
	{ value: 'generic', label: 'Generic', description: 'Applies to all equipment' },
	{ value: 'manufacturer', label: 'Per Manufacturer', description: 'Specific to manufacturer(s)' },
	{ value: 'model', label: 'Per Model', description: 'Specific to equipment model(s)' },
];

// Visibility Options
const VISIBILITIES = [
	{ value: 'internal', label: 'Internal Only', icon: LockClosedIcon },
	{ value: 'public', label: 'Public', icon: GlobeAltIcon },
];

// Status Configuration
const STATUS_CONFIG = {
	draft: {
		label: 'Draft',
		color: 'bg-yellow-100 text-yellow-800',
		icon: ClockIcon,
	},
	published: {
		label: 'Published',
		color: 'bg-green-100 text-green-800',
		icon: CheckCircleIcon,
	},
	archived: {
		label: 'Archived',
		color: 'bg-gray-100 text-gray-800',
		icon: ArchiveBoxIcon,
	},
};

const EditKbArticleModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openDeleteModal, setClose: closeDeleteModal } = useModal('deleteModal');
	const titleInputRef = useRef(null);

	const [form, setForm] = useState({
		title: '',
		summary: '',
		content: '',
		category: null,
		tags: [],
		visibility: 'internal',
		scopeType: 'generic',
		manufacturerIds: [],
		modelIds: [],
	});
	const [tagInput, setTagInput] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [activeTab, setActiveTab] = useState('edit');

	// Templates
	const { value: templates, execute: loadTemplates } = useAsync(getKbTemplates);

	const isEditing = !!open?.article?.id;
	const currentStatus = open?.article?.status || 'draft';

	useEffect(() => {
		if (open?.article) {
			setForm({
				title: open.article.title || '',
				summary: open.article.summary || '',
				content: prepareForEdit(open.article.content || ''),
				category: open.article.category || null,
				tags: open.article.tags || [],
				visibility: open.article.visibility || 'internal',
				scopeType: open.article.scopeType || 'generic',
				manufacturerIds: open.article.manufacturers?.map((m) => m.id) || [],
				modelIds: open.article.models?.map((m) => m.id) || [],
			});
		} else if (open) {
			setForm({
				title: '',
				summary: '',
				content: '',
				category: null,
				tags: [],
				visibility: 'internal',
				scopeType: 'generic',
				manufacturerIds: [],
				modelIds: [],
			});
			loadTemplates();
		}
		setActiveTab('edit');
	}, [open]);

	const handleClose = () => {
		setForm({
			title: '',
			summary: '',
			content: '',
			category: null,
			tags: [],
			visibility: 'internal',
			scopeType: 'generic',
			manufacturerIds: [],
			modelIds: [],
		});
		setTagInput('');
		setActiveTab('edit');
		setClose();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.content.trim()) return;

		setIsSubmitting(true);

		try {
			if (isEditing) {
				await updateKbArticle({
					id: open.article.id,
					...form,
				});
			} else {
				await createKbArticle(form);
			}

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to save article:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = () => {
		if (!open?.article?.id) return;

		openDeleteModal({
			title: 'Delete Article',
			description: `Are you sure you want to delete "${form.title}"? This action cannot be undone.`,
			confirmText: 'Delete Article',
			onConfirm: async () => {
				try {
					await deleteKbArticle({ id: open.article.id });
					closeDeleteModal();
					if (typeof open?.onSuccess === 'function') {
						open.onSuccess();
					}
					handleClose();
				} catch (error) {
					console.error('Failed to delete article:', error);
				}
			},
		});
	};

	const handlePublish = async () => {
		if (!open?.article?.id) return;

		setIsSubmitting(true);
		try {
			// Save first, then publish
			await updateKbArticle({
				id: open.article.id,
				...form,
			});
			await publishKbArticle({ id: open.article.id });

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to publish article:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleArchive = async () => {
		if (!open?.article?.id) return;

		setIsSubmitting(true);
		try {
			await archiveKbArticle({ id: open.article.id });
			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to archive article:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleRestore = async () => {
		if (!open?.article?.id) return;

		setIsSubmitting(true);
		try {
			await restoreKbArticle({ id: open.article.id });
			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to restore article:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAddTag = (e) => {
		if (e.key === 'Enter' && tagInput.trim()) {
			e.preventDefault();
			const newTag = tagInput.trim().toLowerCase();
			if (!form.tags.includes(newTag)) {
				setForm({ ...form, tags: [...form.tags, newTag] });
			}
			setTagInput('');
		}
	};

	const handleRemoveTag = (tagToRemove) => {
		setForm({ ...form, tags: form.tags.filter((tag) => tag !== tagToRemove) });
	};

	const handleApplyTemplate = (template) => {
		if (!template?.structure) return;

		// Build content from template structure using markdown
		const content = template.structure
			.map((section) => `### ${section.heading}\n\n${section.placeholder}`)
			.join('\n\n');

		setForm({
			...form,
			content,
			category: template.category || form.category,
			templateType: template.name,
		});
	};

	const StatusIcon = STATUS_CONFIG[currentStatus]?.icon || ClockIcon;

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={isEditing ? 'Edit Knowledge Base Article' : 'Create Article'}
			size="4xl"
			initialFocusRef={titleInputRef}
		>
			{/* Status Badge */}
			{isEditing && (
				<div className="flex items-center gap-3 mb-6">
					<span className="text-sm text-gray-500">Article Code:</span>
					<span className="font-mono text-sm font-medium">{open?.article?.articleCode}</span>
					<span
						className={classNames(
							'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ml-auto',
							STATUS_CONFIG[currentStatus]?.color || 'bg-gray-100 text-gray-800',
						)}
					>
						<StatusIcon className="w-3.5 h-3.5" />
						{STATUS_CONFIG[currentStatus]?.label || currentStatus}
					</span>
				</div>
			)}

			{/* Templates (only when creating) */}
			{!isEditing && templates?.length > 0 && (
				<div className="mb-6">
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Start from a template
					</label>
					<div className="flex gap-2 flex-wrap">
						{templates.map((template) => (
							<button
								key={template.id}
								type="button"
								onClick={() => handleApplyTemplate(template)}
								className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
							>
								{template.name}
							</button>
						))}
					</div>
				</div>
			)}

			{/* Tabs (only when editing) */}
			{isEditing && (
				<div className="flex border-b border-gray-200 mb-6">
					<button
						type="button"
						onClick={() => setActiveTab('edit')}
						className={classNames(
							'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
							activeTab === 'edit'
								? 'border-indigo-500 text-indigo-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
						)}
					>
						Edit
					</button>
					<button
						type="button"
						onClick={() => setActiveTab('linked')}
						className={classNames(
							'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5',
							activeTab === 'linked'
								? 'border-indigo-500 text-indigo-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
						)}
					>
						<LinkIcon className="h-4 w-4" />
						Linked Items
						{(open?.article?.tickets?.length > 0 || open?.article?.relatedArticles?.length > 0) && (
							<span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
								{(open?.article?.tickets?.length || 0) + (open?.article?.relatedArticles?.length || 0)}
							</span>
						)}
					</button>
				</div>
			)}

			{/* Linked Items Tab */}
			{activeTab === 'linked' && isEditing && (
				<div className="pb-6 space-y-6">
					{/* Linked Tickets */}
					<div>
						<h3 className="text-sm font-medium text-gray-700 mb-3">Linked Tickets</h3>
						{open?.article?.tickets?.length > 0 ? (
							<div className="space-y-2">
								{open.article.tickets.map((ticket) => (
									<div
										key={ticket.id}
										className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
									>
										<div>
											<span className="font-medium text-sm">{ticket.subject}</span>
											<span className="text-xs text-gray-500 ml-2">
												{ticket.kb_article_ticket?.link_type === 'source'
													? '(Source)'
													: ticket.kb_article_ticket?.link_type === 'resolves'
														? '(Resolves)'
														: '(Related)'}
											</span>
										</div>
										<Badge preset="status" value={ticket.status} />
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-500 italic">No linked tickets</p>
						)}
					</div>

					{/* Related Articles */}
					<div>
						<h3 className="text-sm font-medium text-gray-700 mb-3">Related Articles</h3>
						{open?.article?.relatedArticles?.length > 0 ? (
							<div className="space-y-2">
								{open.article.relatedArticles.map((article) => (
									<div
										key={article.id}
										className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
									>
										<div>
											<span className="font-mono text-xs text-gray-500 mr-2">
												{article.articleCode}
											</span>
											<span className="font-medium text-sm">{article.title}</span>
										</div>
										<Badge preset="status" value={article.status} />
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-500 italic">No related articles</p>
						)}
					</div>
				</div>
			)}

			{/* Edit Tab */}
			{activeTab === 'edit' && (
				<form onSubmit={handleSubmit} className="pb-6">
					{/* Title */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Title <span className="text-red-500">*</span>
						</label>
						<input
							ref={titleInputRef}
							type="text"
							value={form.title}
							onChange={(e) => setForm({ ...form, title: e.target.value })}
							placeholder="Enter article title"
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
							required
						/>
					</div>

					{/* Summary */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Summary
						</label>
						<textarea
							value={form.summary}
							onChange={(e) => setForm({ ...form, summary: e.target.value })}
							placeholder="Brief description for search results..."
							rows={2}
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
						/>
					</div>

					{/* Content */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Content <span className="text-red-500">*</span>
						</label>
						<MarkdownEditor
							value={form.content}
							onChange={(content) => setForm({ ...form, content })}
							placeholder="Write your article content here..."
							uploadImage={uploadKbImage}
						/>
					</div>

					{/* Category and Visibility Row */}
					<div className="grid grid-cols-2 gap-4 mb-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Category
							</label>
							<FilterDropdown
								placeholder="Select category..."
								value={form.category}
								options={CATEGORIES}
								onChange={(value) => setForm({ ...form, category: value })}
								clearable
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Visibility
							</label>
							<FilterDropdown
								placeholder="Select visibility..."
								value={form.visibility}
								options={VISIBILITIES}
								onChange={(value) => setForm({ ...form, visibility: value })}
							/>
						</div>
					</div>

					{/* Scope Type */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Scope
						</label>
						<div className="flex gap-3">
							{SCOPE_TYPES.map((scope) => (
								<button
									key={scope.value}
									type="button"
									onClick={() => setForm({ ...form, scopeType: scope.value })}
									className={classNames(
										'flex-1 p-3 rounded-lg border-2 text-left transition-colors',
										form.scopeType === scope.value
											? 'border-indigo-500 bg-indigo-50'
											: 'border-gray-200 hover:border-gray-300',
									)}
								>
									<div className="font-medium text-sm">{scope.label}</div>
									<div className="text-xs text-gray-500">{scope.description}</div>
								</button>
							))}
						</div>
					</div>

					{/* Manufacturer/Model Selection (when not generic) */}
					{form.scopeType !== 'generic' && (
						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								{form.scopeType === 'manufacturer' ? 'Select Manufacturers' : 'Select Models'}
							</label>
							<ManufacturerModelSelect
								manufacturerIds={form.manufacturerIds}
								modelIds={form.modelIds}
								onManufacturerChange={(ids) => setForm({ ...form, manufacturerIds: ids })}
								onModelChange={(ids) => setForm({ ...form, modelIds: ids })}
							/>
						</div>
					)}

					{/* Tags */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Tags
						</label>
						<div className="flex flex-wrap gap-2 mb-2">
							{form.tags.map((tag) => (
								<span
									key={tag}
									className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm"
								>
									<TagIcon className="w-3 h-3 text-gray-500" />
									{tag}
									<button
										type="button"
										onClick={() => handleRemoveTag(tag)}
										className="text-gray-400 hover:text-gray-600"
									>
										<XMarkIcon className="w-3 h-3" />
									</button>
								</span>
							))}
						</div>
						<input
							type="text"
							value={tagInput}
							onChange={(e) => setTagInput(e.target.value)}
							onKeyDown={handleAddTag}
							placeholder="Type tag and press Enter..."
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
						/>
					</div>

					{/* Actions */}
					<div className="flex justify-between items-center pt-4 border-t border-gray-200">
						{/* Left side - Delete/Archive */}
						<div className="flex gap-2">
							{isEditing && currentStatus !== 'archived' && (
								<button
									type="button"
									onClick={handleArchive}
									disabled={isSubmitting}
									className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
								>
									<ArchiveBoxIcon className="w-4 h-4" />
									Archive
								</button>
							)}
							{isEditing && currentStatus === 'archived' && (
								<button
									type="button"
									onClick={handleRestore}
									disabled={isSubmitting}
									className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
								>
									Restore
								</button>
							)}
							{isEditing && (
								<button
									type="button"
									onClick={handleDelete}
									className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 shadow-sm hover:bg-red-50"
								>
									<TrashIcon className="w-4 h-4" />
									Delete
								</button>
							)}
						</div>

						{/* Right side - Cancel, Save, Publish */}
						<div className="flex gap-3">
							<button
								type="button"
								onClick={handleClose}
								className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={!form.title.trim() || !form.content.trim() || isSubmitting}
								className={classNames(
									'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
									!form.title.trim() || !form.content.trim() || isSubmitting
										? 'bg-gray-400 cursor-not-allowed'
										: 'bg-indigo-600 hover:bg-indigo-700',
								)}
							>
								{isSubmitting ? 'Saving...' : isEditing ? 'Save Draft' : 'Create Article'}
							</button>

							{isEditing && currentStatus !== 'published' && (
								<button
									type="button"
									onClick={handlePublish}
									disabled={isSubmitting || !form.title.trim() || !form.content.trim()}
									className={classNames(
										'inline-flex items-center rounded-md px-4 py-2 text-sm font-medium shadow-sm',
										isSubmitting || !form.title.trim() || !form.content.trim()
											? 'bg-gray-100 text-gray-400 cursor-not-allowed'
											: 'bg-green-600 text-white hover:bg-green-700',
									)}
								>
									Publish
								</button>
							)}
						</div>
					</div>
				</form>
			)}
		</StackableModal>
	);
};

export default EditKbArticleModal;
